import React from 'react';
import { Heart, Activity, User, Calendar, Thermometer } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета максимального пульса
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета максимального пульса">
      {/* Ключевая метрика - максимальный пульс */}
      <div className="rounded-lg bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 p-5 border border-red-100 dark:border-red-800/50 shadow-sm mb-6">
        <h3 className="text-base font-medium text-red-700 dark:text-red-300 mb-1.5 flex items-center gap-1.5">
          <Heart className="w-4 h-4"/>
          Ваш максимальный пульс (MHR)
        </h3>
        <div className="flex items-baseline flex-wrap gap-x-2">
          <span className="text-3xl font-bold text-red-800 dark:text-red-200 pulse-animation" aria-live="polite">
            {results.maxHeartRate} уд/мин
          </span>
        </div>
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          Рассчитано по формуле {results.formulaName} с учетом ваших персональных параметров
        </p>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Возраст" 
          value={`${results.age} лет`} 
          color="orange" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Пол" 
          value={results.gender === 'male' ? 'Мужской' : 'Женский'} 
          color="orange" 
          icon={<User className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Формула расчета" 
          value={results.formulaName} 
          icon={<Activity className="w-4 h-4"/>} 
        />
        
        {results.restingHeartRate && (
          <ResultItem 
            label="Пульс в покое" 
            value={`${results.restingHeartRate} уд/мин`} 
            color="green"
            icon={<Heart className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Уровень активности" 
          value={results.activityLevelName} 
          icon={<Activity className="w-4 h-4"/>} 
        />
        
        {results.fitnessLevel && (
          <ResultItem 
            label="Уровень подготовки" 
            value={results.fitnessLevelName} 
            color="blue" 
          />
        )}
        
        <ResultItem 
          label="Вид физической активности" 
          value={results.sportSpecificName} 
          color="blue" 
        />
      </div>

      {/* Блок с рекомендациями */}
      <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-5 border border-green-100 dark:border-green-800">
        <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
          <Thermometer className="w-5 h-5" />
          Рекомендации по тренировкам
        </h3>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 dark:text-green-300 text-sm font-medium">1</span>
            </div>
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                {results.recommendations.warmUp}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Используйте эту зону для разминки и расслабления
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 dark:text-green-300 text-sm font-medium">2</span>
            </div>
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                {results.recommendations.fatBurning}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Эта зона оптимальна для сжигания жира и длительных тренировок на выносливость
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 dark:text-green-300 text-sm font-medium">3</span>
            </div>
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                {results.recommendations.cardio}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Зона для улучшения кардио-выносливости и тренировки сердечно-сосудистой системы
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 dark:text-green-300 text-sm font-medium">4</span>
            </div>
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                {results.recommendations.anaerobic}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Анаэробная зона для коротких интенсивных тренировок и интервалов
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700">
          <p className="text-sm text-green-700 dark:text-green-300">
            <span className="font-medium">Общая рекомендация:</span> {results.recommendations.general}
          </p>
          <p className="text-sm text-green-700 dark:text-green-300 mt-2">
            <span className="font-medium">После тренировки:</span> {results.recommendations.recovery}
          </p>
        </div>
      </div>

      {/* Предупреждение о точности */}
      <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <div>
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">Важное примечание</h4>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
              Все формулы дают приблизительные результаты. Точность расчета может варьироваться до ±10-15 ударов в минуту. 
              Если вы только начинаете заниматься физической активностью, начинайте с нижних границ пульсовых зон и постепенно 
              повышайте интенсивность. При наличии заболеваний сердечно-сосудистой системы обязательно 
              проконсультируйтесь с врачом перед тренировками.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;